import React, { useState, useMemo } from "react";
import { PageLoader } from "../../../Components/Loader/PageLoader";
import {
  Box, Card, Grid, Typography, TextField, MenuItem, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Chip, Button, IconButton, Tooltip, InputAdornment, CircularProgress,
} from "@mui/material";
import { CiSearch } from "react-icons/ci";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useGetShopInvoicesQuery } from "../../../Api/attoDeskApi";
import { IShopInvoice } from "../../../Api/Interface/api.interface";
import dayjs from "dayjs";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import Pagination from "../../../Components/Pagination";
import logoBlack from "../../../Images/logo_main_black.webp";

const STATUS_CHIP: Record<string, { label: string; bg: string; color: string }> = {
  unpaid:  { label: "Unpaid",  bg: "#ffebee", color: "#c62828" },
  partial: { label: "Partial", bg: "#fff8e1", color: "#f57f17" },
  paid:    { label: "Paid",    bg: "#e8f5e9", color: "#2e7d32" },
};

const ICON_BTN_SX = {
  width: 32,
  height: 32,
  borderRadius: "8px",
  bgcolor: "#f8fafc",
  color: "#0B1F3A",
  border: "1px solid #e2e8f0",
  transition: "all 0.15s ease",
  "&:hover": { bgcolor: "#e2e8f0" },
} as const;

const PRINT_STYLES = `
@media print {
  .no-print, .sidebar, header, nav, .MuiDrawer-root, .filters, .action-buttons { display: none !important; }
  body { background: #fff !important; }
  .print-only { display: block !important; }
  table { font-size: 12px; border-collapse: collapse; width: 100%; }
  th, td { padding: 8px; border: 1px solid #ddd !important; }
  .MuiCard-root { box-shadow: none !important; border: none !important; }
}
.print-only { display: none; }
`;

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const debouncedSearch = useDebounce(search, 300);

  const { data, isLoading } = useGetShopInvoicesQuery({
    searchText: debouncedSearch,
    payment_status: statusFilter !== "all" ? statusFilter : undefined,
  });

  const invoices: IShopInvoice[] = useMemo(() => {
    return Array.isArray(data?.data) ? data!.data : [];
  }, [data]);

  const totals = useMemo(() => {
    return invoices.reduce((acc, inv) => ({
      total: acc.total + inv.total,
      paid: acc.paid + inv.amount_paid,
      due: acc.due + inv.balance_due,
    }), { total: 0, paid: 0, due: 0 });
  }, [invoices]);

  const handlePrintList = () => {
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) return;
    const logoUrl = (logoBlack as string).startsWith("http") || (logoBlack as string).startsWith("data:")
      ? (logoBlack as string)
      : `${window.location.origin}${logoBlack}`;
    const reportDate = dayjs().format("MMMM DD, YYYY");
    const preparedBy = localStorage.getItem("user_email") || localStorage.getItem("email") || "—";
    const filterLabel = statusFilter === "all" ? "All Invoices" : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1);
    const statusPill = (s: string) => {
      const k = (s || "").toLowerCase();
      const cls = k === "paid" ? "paid" : k === "partial" ? "partial" : "unpaid";
      const label = k.charAt(0).toUpperCase() + k.slice(1);
      return `<span class="status ${cls}">${label}</span>`;
    };
    const rowsHtml = invoices.map((inv: any, i: number) => `
      <tr>
        <td class="num">${i + 1}</td>
        <td class="invno"><div class="invno-main">${inv.invoice_number}</div></td>
        <td>${dayjs(inv.invoice_date).format("MMM D, YYYY")}</td>
        <td class="client">${(inv.shop?.customerCompanyName || `Shop #${inv.shop_id}`)}</td>
        <td class="right amt">$${Number(inv.total).toFixed(2)}</td>
        <td class="right paid-amt">$${Number(inv.amount_paid).toFixed(2)}</td>
        <td class="right bal">$${Number(inv.balance_due).toFixed(2)}</td>
        <td>${statusPill(inv.payment_status)}</td>
      </tr>
    `).join("");
    w.document.write(`<!DOCTYPE html><html><head><title>Mr. Veggie Ltd Invoices Report</title>
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1f2937;background:#fff;padding:36px 40px}
  .report{max-width:1180px;margin:0 auto}
  .report-header{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;margin-bottom:18px}
  .header-left .logo{height:46px;width:auto;display:block;margin-bottom:10px}
  .header-left .meta{font-size:12px;color:#475569;line-height:1.7}
  .header-right{text-align:right}
  .header-right .title{font-size:26px;font-weight:800;color:#0B1F3A;letter-spacing:.6px;font-family:Georgia,'Times New Roman',serif}
  .header-right .meta{font-size:12px;color:#475569;line-height:1.7;margin-top:4px}
  .filter-strip{background:#eff4fb;border-top:1px solid #d6e1ef;border-bottom:1px solid #d6e1ef;padding:10px 14px;font-size:12px;color:#0B1F3A;margin-bottom:18px}
  .filter-strip strong{font-weight:700;margin-right:6px}
  .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:18px}
  .sum-card{border:1px solid;padding:18px 20px;border-radius:4px;text-align:center}
  .sum-card .label{font-size:10.5px;font-weight:700;letter-spacing:1.2px;color:#64748b;margin-bottom:8px}
  .sum-card .value{font-size:22px;font-weight:800;font-family:Georgia,'Times New Roman',serif}
  .sum-card.invoices{background:#f3f6fb;border-color:#dde5f0}
  .sum-card.invoices .value{color:#0B1F3A}
  .sum-card.amount{background:#eef4fc;border-color:#cfdcef}
  .sum-card.amount .value{color:#1d4ed8}
  .sum-card.paid{background:#eafaf0;border-color:#c5ecd2}
  .sum-card.paid .value{color:#15803d}
  .sum-card.balance{background:#fdf3e7;border-color:#f3d8b5}
  .sum-card.balance .value{color:#b45309}
  table{width:100%;border-collapse:collapse;font-size:12.5px}
  thead th{background:#0B1F3A;color:#fff;font-size:10.5px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;padding:12px 12px;text-align:left;border-bottom:1px solid #0B1F3A}
  thead th.right{text-align:right}
  tbody td{padding:14px 12px;border-bottom:1px solid #e5e7eb;vertical-align:middle}
  tbody td.num{color:#94a3b8;font-size:11px;width:36px}
  tbody td.right{text-align:right}
  tbody td.invno-main,.invno-main{font-weight:700;color:#0B1F3A}
  tbody td.client{font-weight:600;color:#1f2937}
  tbody td.amt{font-weight:700;color:#1f2937}
  tbody td.paid-amt{font-weight:700;color:#15803d}
  tbody td.bal{font-weight:700;color:#0B1F3A}
  .status{display:inline-block;padding:3px 10px;border-radius:4px;font-size:11px;font-weight:700;text-transform:capitalize}
  .status.paid{color:#15803d}
  .status.partial{color:#b45309}
  .status.unpaid{color:#b91c1c}
  tfoot td{background:#0B1F3A;color:#fff;font-size:12.5px;font-weight:700;padding:14px 12px;letter-spacing:.4px}
  tfoot td.right{text-align:right}
  .divider{height:1px;background:#e5e7eb;margin:28px 0 14px 0}
  .footer{text-align:center;font-size:11px;color:#64748b;padding-top:6px}
  .footer .brand{color:#0B1F3A;font-weight:700}
  @media print{body{padding:18px}}
</style>
</head><body>
<div class="report">
  <div class="report-header">
    <div class="header-left">
      <img src="${logoUrl}" alt="Mr. Veggie Ltd" class="logo" />
      <div class="meta">
        Mr. Veggie Ltd — Fresh From The Earth<br/>
        support@cacart.com<br/>
      </div>
    </div>
    <div class="header-right">
      <div class="title">INVOICES REPORT</div>
      <div class="meta">
        Date: ${reportDate}<br/>
        Prepared By: ${preparedBy}
      </div>
    </div>
  </div>
  <div class="filter-strip"><strong>Filter:</strong>${filterLabel}</div>
  <div class="summary">
    <div class="sum-card invoices"><div class="label">TOTAL INVOICES</div><div class="value">${invoices.length}</div></div>
    <div class="sum-card amount"><div class="label">TOTAL AMOUNT</div><div class="value">$${totals.total.toFixed(2)}</div></div>
    <div class="sum-card paid"><div class="label">TOTAL PAID</div><div class="value">$${totals.paid.toFixed(2)}</div></div>
    <div class="sum-card balance"><div class="label">TOTAL BALANCE</div><div class="value">$${totals.due.toFixed(2)}</div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Invoice No</th>
        <th>Date</th>
        <th>Restaurant</th>
        <th class="right">Amount</th>
        <th class="right">Paid</th>
        <th class="right">Balance</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>${rowsHtml || `<tr><td colspan="8" style="text-align:center;padding:24px;color:#94a3b8">No invoices</td></tr>`}</tbody>
    <tfoot>
      <tr>
        <td colspan="4">TOTALS &nbsp;|&nbsp; ${invoices.length} invoice${invoices.length === 1 ? "" : "s"}</td>
        <td class="right">$${totals.total.toFixed(2)}</td>
        <td class="right">$${totals.paid.toFixed(2)}</td>
        <td class="right">$${totals.due.toFixed(2)}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
  <div class="divider"></div>
  <div class="footer">
    <div><span class="brand">Mr. Veggie Ltd</span> — Smart Inventory & Order Management</div>
    <div>Generated via Mr. Veggie Ltd System &nbsp;•&nbsp; © ${dayjs().format("YYYY")}</div>
  </div>
</div>
<script>window.onload=function(){window.print()}</script>
</body></html>`);
    w.document.close();
  };

  if (isLoading) return <PageLoader height="30vh" />;

  return (
    <div>
      <style>{PRINT_STYLES}</style>
      <Box className="print-only" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0B1F3A" }}>Mr. Veggie Ltd – Invoices List</Typography>
        <Typography sx={{ fontSize: 11, color: "#64748b" }}>Generated {dayjs().format("MMM D, YYYY h:mm A")}</Typography>
      </Box>
      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 2, mt: 1 }} className="no-print">
        {[
          { label: "Total Invoiced", value: totals.total, color: "#1a237e" },
          { label: "Total Received", value: totals.paid, color: "#2e7d32" },
          { label: "Total Outstanding", value: totals.due, color: "#c62828" },
        ].map(c => (
          <Grid item xs={12} sm={4} key={c.label}>
            <Card sx={{ p: 2.5, borderRadius: 2, borderLeft: `4px solid ${c.color}` }}>
              <Typography fontSize={11} color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>{c.label}</Typography>
              <Typography fontSize={22} fontWeight={800} color={c.color}>${c.value.toFixed(2)}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Card className={classes.card}>
            <Grid container justifyContent="space-between" spacing={2} sx={{ borderBottom: 2, padding: 2 }} className={classes.titleGrid}>
              <Grid item lg={3} md={4} sm={6} xs={12} alignContent="center">
                <Typography className={classes.cardTitle}>Restaurant Invoices</Typography>
              </Grid>
              <Grid item lg={9} md={8} sm={6} xs={12} sx={{ display: "flex", justifyContent: "flex-end", gap: 1, alignItems: "center" }} className="no-print">
                <TextField
                  select size="small" value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="unpaid">Unpaid</MenuItem>
                  <MenuItem value="partial">Partial</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                </TextField>
                <TextField
                  size="small" placeholder="Search invoice or restaurant..."
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch style={{ fontSize: 16, color: "#999" }} /></InputAdornment> }}
                  sx={{ minWidth: 260 }}
                />
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
                  onClick={handlePrintList}
                  sx={{
                    bgcolor: "#0B1F3A", color: "#fff", height: 36, borderRadius: "8px",
                    textTransform: "none", fontWeight: 600, fontSize: 13, px: 2,
                    "&:hover": { bgcolor: "#0F2A4A" },
                  }}
                >
                  Print List
                </Button>
              </Grid>
            </Grid>

            <TableContainer>
              <Table className={classes.table}>
                <TableHead className={classes.tableHeaderRow}>
                  <TableRow>
                    <TableCell className={classes.tablheader}><strong>#</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Invoice</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Restaurant</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Date</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Total</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Paid</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Balance</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Status</strong></TableCell>
                    <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center">No invoices found</TableCell></TableRow>
                  ) : (
                    invoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((inv, i) => {
                      const s = STATUS_CHIP[inv.payment_status] || STATUS_CHIP.unpaid;
                      return (
                        <TableRow key={inv.id} className={classes.tableRowHover} sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/shop-invoice/${inv.id}`)}>
                          <TableCell className={classes.tablCell}>{page * rowsPerPage + i + 1}</TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={700} color="#1a237e">{inv.invoice_number}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13}>{(inv.shop as any)?.customerCompanyName || `Shop #${inv.shop_id}`}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={12}>{dayjs(inv.invoice_date).format("MMM D, YYYY")}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={600}>${inv.total.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={600} color="#2e7d32">${inv.amount_paid.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Typography fontSize={13} fontWeight={600} color={inv.balance_due > 0 ? "#c62828" : "#2e7d32"}>
                              ${inv.balance_due.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            <Chip label={s.label} size="small" sx={{ fontSize: 11, fontWeight: 700, bgcolor: s.bg, color: s.color }} />
                          </TableCell>
                          <TableCell className={classes.tablCell} sx={{ textAlign: "right", verticalAlign: "middle" }}>
                            <Box className="action-buttons" sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "flex-end" }}>
                              <Tooltip title="View">
                                <IconButton size="small" sx={ICON_BTN_SX}
                                  onClick={(e) => { e.stopPropagation(); navigate(`/shop-invoice/${inv.id}`); }}>
                                  <VisibilityIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Print">
                                <IconButton size="small" sx={ICON_BTN_SX}
                                  onClick={(e) => { e.stopPropagation(); navigate(`/shop-invoice/${inv.id}?print=1`); }}>
                                  <PrintIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton size="small" sx={ICON_BTN_SX}
                                  onClick={(e) => { e.stopPropagation(); navigate(`/shop-invoice/${inv.id}`); }}>
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination count={invoices.length} page={page} rowsPerPage={rowsPerPage}
              onPageChange={(_: any, p: number) => setPage(p)} />
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
